/**
 * Groups Management Page
 * 
 * Main page for managing groups with features:
 * - Group list with pagination
 * - Create/Edit/Delete groups
 * - Status management
 * - Manage agents and certificates
 * 
 * Uses StandardPage template for consistent layout
 */

import { FC, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { useCrudPage } from '@/hooks';
import { groupService } from '@/services';
import StandardPage from '@/components/Common/StandardPage';
import GroupTable from '@/components/GroupTable/GroupTable';
import Modal from '@/components/Common/Modal';
import GroupForm from '@/components/Forms/GroupForm';
import GroupManagementModal from '@/components/GroupManagementModal';
import type { GroupFormData } from '@/components/Forms/GroupForm';
import type { GroupListItem } from '@/types';

const GroupsPage: FC = () => {
  const navigate = useNavigate();

  /**
   * State for management modal
   */
  const [isManageModalOpen, setIsManageModalOpen] = useState(false);
  const [selectedGroupForManage, setSelectedGroupForManage] = useState<GroupListItem | null>(null);

  /**
   * Use CRUD page hook
   */
  const {
    data: groups,
    total,
    isLoading,
    isError,
    error,
    pagination,
    setPagination,
    isModalOpen,
    modalMode,
    selectedItem: selectedGroup,
    mutations: { create: createMutation, update: updateMutation },
    refetch,
    handleOpenCreate,
    handleOpenEdit,
    handleCancelModal,
  } = useCrudPage({
    queryKey: 'groups',
    service: {
      getList: (filters: any) =>
        groupService.getGroups(
          {
            page: filters.page,
             size: filters.size,
             sort: filters.sortBy && filters.sortOrder ? `${filters.sortBy},${filters.sortOrder}` : undefined,

          },
          {}
        ),
      create: groupService.createGroup,
      update: groupService.updateGroup,
      delete: groupService.deleteGroup,
    },
    initialPageSize: 10,
    successMessages: {
      create: {
        title: 'Grupo criado!',
        message: 'O grupo foi criado com sucesso.',
      },
      update: {
        title: 'Grupo atualizado!',
        message: 'As alterações foram salvas com sucesso.',
      },
      delete: {
        title: 'Grupo deletado!',
        message: 'O grupo foi removido com sucesso.',
      },
    },
  });

  /**
   * Handle view group detail
   */
  const handleView = useCallback(
    (group: GroupListItem) => {
      navigate(`/groups/${group.ref}`);
    },
    [navigate]
  );

  /**
   * Handle manage group (agents and certificates)
   */
  const handleManage = useCallback(
    (group: GroupListItem) => {
      setSelectedGroupForManage(group);
      setIsManageModalOpen(true);
    },
    []
  );

  /**
   * Handle close manage modal
   */
  const handleCloseManageModal = useCallback(() => {
    setIsManageModalOpen(false);
    setSelectedGroupForManage(null);
  }, []);

  /**
   * Handle form submit
   */
  const handleFormSubmit = useCallback(
    async (data: GroupFormData) => {
      if (modalMode === 'create') {
        await createMutation.mutateAsync(data);
      } else if (selectedGroup) {
        await updateMutation.mutateAsync({
          id: selectedGroup.id,
          data,
        });
      }
    },
    [modalMode, selectedGroup, createMutation, updateMutation]
  );

  return (
    <>
      <StandardPage
        header={{
          title: 'Gerenciamento de Grupos',
          subtitle: 'Gerencie os grupos do sistema',
          actions: [
            {
              label: 'Novo Grupo',
              icon: <FiPlus size={18} />,
              onClick: handleOpenCreate,
              variant: 'primary',
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRefetch={refetch}
        pagination={{
          state: pagination,
          data: {
            total,
            totalPages: Math.ceil(total / pagination.pageSize),
            page: pagination.pageIndex,
            pageSize: pagination.pageSize,
          },
          onChange: setPagination,
        }}
      >
        <GroupTable
          groups={groups}
          loading={false}
          onEdit={handleOpenEdit}
          onView={handleView}
          onManage={handleManage}
        />
      </StandardPage>

      {/* Create/Edit Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCancelModal}
        title={modalMode === 'create' ? 'Criar Novo Grupo' : 'Editar Grupo'}
        description={
          modalMode === 'create'
            ? 'Preencha os dados para criar um novo grupo'
            : 'Atualize as informações do grupo'
        }
        size="md"
      >
        <GroupForm
          mode={modalMode}
          initialValues={selectedGroup || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>

      {/* Management Modal (Agents & Certificates) */}
      {selectedGroupForManage && (
        <GroupManagementModal
          open={isManageModalOpen}
          onClose={handleCloseManageModal}
          groupId={selectedGroupForManage.id}
          groupName={selectedGroupForManage.name}
          onUpdate={refetch}
        />
      )}
    </>
  );
};

export default GroupsPage;
